import type { ReactNode } from 'react';
import { DateTime } from 'luxon';
import { Link } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import { useTenant } from '@/hooks/use-tenant';
import { getLuxonLocale } from '@/lib/utils';
import { NotificationPreview } from '@/components/notification-preview';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  formatSaleAmount,
  formatSaleDateTime,
  formatSaleStatus,
} from '@/lib/sales-history';
import type { SalesHistorySale } from '@/components/sales-history/sale-card';

interface SaleDetailSheetProps {
  sale: SalesHistorySale | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/**
 * Every field of the Vendas table for one order (SALES-07 AC 2). Customer
 * name, phone and e-mail only with PII access, as the table columns.
 */
export function SaleDetailSheet({
  sale,
  open,
  onOpenChange,
}: SaleDetailSheetProps) {
  const { LL, locale } = useI18nContext();
  const tenant = useTenant();
  const canShowPii = tenant.hasAccessToPii !== false;

  return (
    <Drawer open={open && sale !== null} onOpenChange={onOpenChange}>
      <DrawerContent>
        {sale && (
          <>
            <DrawerHeader className="text-left">
              <DrawerTitle>{LL.salesPage.mobile.details()}</DrawerTitle>
              <DrawerDescription>{sale.orderNumber}</DrawerDescription>
            </DrawerHeader>
            <dl className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-6">
              <Field label={LL.salesPage.table.date()}>
                {formatSaleDateTime(sale.date, locale)} -{' '}
                {DateTime.fromISO(sale.date)
                  .setLocale(getLuxonLocale(locale))
                  .toRelative()}
              </Field>
              <Field label={LL.salesPage.table.orderNumber()}>
                {sale.orderNumber}
              </Field>
              <Field label={LL.salesPage.table.amount()}>
                <span className="font-medium tabular-nums">
                  {formatSaleAmount(sale, locale)}
                </span>
              </Field>
              <Field label={LL.salesPage.table.situation()}>
                {formatSaleStatus(sale.paymentStatus, LL)}
              </Field>
              <Field label={LL.salesPage.table.product()}>
                <ul className="flex flex-col gap-2">
                  {sale.lineItemNames.map((name, i) => (
                    <li
                      key={sale.lineItemExternalIds[i] ?? i}
                      data-sale-product=""
                      className="flex flex-col"
                    >
                      <span>{name}</span>
                      <span className="text-xs text-muted-foreground">
                        {LL.salesPage.table.quantity()}:{' '}
                        {sale.lineItemQuantities[i]}
                        {' · '}
                        {LL.salesPage.table.sku()}: {sale.lineItemSkus[i]}
                      </span>
                    </li>
                  ))}
                </ul>
              </Field>
              {sale.campaignName && (
                <Field label={LL.salesPage.table.campaign()}>
                  <div className="flex flex-col gap-2">
                    <Link
                      to={`/${tenant.slug}/campaigns/${sale.campaignId}`}
                      className="font-medium text-primary underline-offset-4 hover:underline"
                    >
                      {sale.campaignName}
                    </Link>
                    <NotificationPreview
                      title={sale.campaignNotificationTitle ?? ''}
                      body={sale.campaignNotificationBody ?? ''}
                      iconUrl={tenant.iconUrl}
                    />
                  </div>
                </Field>
              )}
              <Field label={LL.salesPage.table.utmTag()}>
                <span className="break-all">
                  utm_source={sale.utmSource},utm_campaign={sale.utmCampaign}
                </span>
              </Field>
              {canShowPii && (
                <>
                  <Field label={LL.salesPage.table.customerName()}>
                    {sale.customerName}
                  </Field>
                  <Field label={LL.salesPage.table.customerPhone()}>
                    {sale.customerPhone}
                  </Field>
                  <Field label={LL.salesPage.table.customerEmail()}>
                    {sale.customerEmail}
                  </Field>
                </>
              )}
            </dl>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
